import { PermissionScope } from 'types/apps';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { ReactElement, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import SegmentInlineFieldButton from 'ui-component/Segment/SegmentInlineFieldButton';
import * as Yup from 'yup';
import { UseMutation } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { handleErr } from 'utils/functions';
import { IconArrowBackUp, IconDeviceFloppy, IconX } from '@tabler/icons-react';
import SegmentIconButton from 'ui-component/Segment/SegmentIconButton';
import _ from 'lodash';
import useSnackbar from 'hooks/useSnackbar';
import { CircularProgress, GridLegacy } from '@mui/material';
import { useInlineEditClickHandling } from 'hooks/useInlineEditClickHandling';

export const EditInlineSx = {
  my: 0,
  marginTop: '0px',
  marginBottom: '0px',
  height: '42px',
  '.MuiOutlinedInput-notchedOutline': {
    borderBottomRightRadius: '0px',
    borderTopRightRadius: '0px',
  },
  '& .MuiOutlinedInput-root': {
    height: '42px',
  },
  '& .MuiInputLabel-root': {
    lineHeight: '1.75em',
  },
  '& .MuiAutocomplete-inputRoot': {
    height: 'auto',
  },
  '& .MuiChip-root': {
    height: 'unset',
  },
  '& .MuiAutocomplete-input': {
    padding: '2px !important',
  },
};

export const ClickOffEditInlineSx = {
  my: 0,
  marginTop: '0px',
  marginBottom: '0px',
  height: '42px',
  '& .MuiOutlinedInput-root': {
    height: '42px',
  },
  '& .MuiInputLabel-root': {
    lineHeight: '1.75em',
  },
  '& .MuiAutocomplete-inputRoot': {
    height: 'auto',
  },
  '& .MuiChip-root': {
    height: 'unset',
  },
  '& .MuiAutocomplete-input': {
    padding: '2px !important',
  },
};

export const EditInlineBoxSx = {
  my: 0,
  height: '42px',
  marginBottom: '0px',
};

export const EditInlineButtonGroupSx = {
  height: '42px',
  marginBottom: '0px',
};

type EditInlineTrackingNamesType = {
  cancel?: string;
  save?: string;
};

interface BaseEditInlineProps<
  TInput = string | null,
  TOutput = string | null | unknown
> {
  name: string;
  validation?: Yup.SchemaOf<unknown, object>;
  value?: unknown;
  /**
   * This should be a single `HookFormComponent`
   */
  children: ReactElement;
  permissionScope?: PermissionScope;
  // the `any` here seems to be the type used in the RTK Query type definitions
  useMutation: UseMutation<any>;
  id: string;
  closeEditing?: () => void;
  onSave?: () => void;
  /**
   * Optional pre-processing function to be applied to the property before submitting
   */
  preSubmit?: (data: TInput) => TOutput;
  idProp?: string;
  otherProps?: object;
  trackingNames?: EditInlineTrackingNamesType;
  submitAsClientV2Api?: boolean;
  fullHeight?: boolean;
  nullable?: boolean;
  allowDefaultValueZero?: boolean;
  disableClickOffInlineEdit?: boolean;
}

type EditInlineProps<
  TInput = string | null,
  TOutput = string | null | unknown
> = BaseEditInlineProps<TInput, TOutput> &
  (
    | {
        hideLabel?: true;
        label?: string;
      }
    | {
        hideLabel?: false;
        label: string;
      }
  );

const EditInline = <TInput = string | null, TOutput = string | null | unknown>({
  name,
  label,
  hideLabel,
  validation,
  value,
  children,
  permissionScope,
  useMutation,
  id,
  closeEditing,
  onSave,
  preSubmit,
  idProp = 'id',
  otherProps = {},
  trackingNames,
  submitAsClientV2Api,
  fullHeight,
  nullable = false,
  allowDefaultValueZero = false,
  disableClickOffInlineEdit = false,
}: EditInlineProps<TInput, TOutput>) => {
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();

  const [mutate, { isLoading }] = useMutation();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      [name]: value || (allowDefaultValueZero && value === 0 ? 0 : ''),
    },
    ...(validation
      ? {
          resolver: yupResolver(
            Yup.object().shape({
              [name]: validation,
            })
          ),
        }
      : {}),
  });

  const onSubmit = async (data: FieldValues) => {
    try {
      let property = _.get(data, name);

      if (nullable && property === '') {
        property = null;
      }

      if (mutate) {
        let payload;
        if (preSubmit) {
          const preSubmitResult = preSubmit(property as TInput);
          if (Array.isArray(preSubmitResult)) {
            payload = _.cloneDeep(preSubmitResult);
          } else {
            payload = _.isObject(preSubmitResult)
              ? _.cloneDeep(preSubmitResult)
              : { [name]: preSubmitResult };
          }
        } else {
          payload = { [name]: property };
        }
        const mutatePayload = Array.isArray(payload)
          ? payload
          : submitAsClientV2Api
          ? { ..._.cloneDeep(otherProps), ...payload, id }
          : { ..._.cloneDeep(otherProps), payload, [idProp]: id };
        await mutate(mutatePayload).unwrap();
        dispatchSuccessSnackbar(`${label ?? 'Record'} updated`);
        onSave && onSave();
      }
    } catch (err) {
      handleErr(err, (errMessage: string) => {
        dispatchErrorSnackbar(`Failed to update ${label}: ${errMessage}`);
      });
    }
    closeEditing && closeEditing();
  };

  useEffect(() => {
    reset({
      [name]: value || (allowDefaultValueZero && value === 0 ? 0 : ''),
    });
  }, [value, name, reset, allowDefaultValueZero]);

  const { formRef, tempClickOffInlineEdit } = useInlineEditClickHandling({
    handleSubmit,
    onSubmit: () => handleSubmit(onSubmit)(),
    disableClickOffInlineEdit,
  });

  const inlineSx = tempClickOffInlineEdit ? ClickOffEditInlineSx : EditInlineSx;

  const clonedChild = React.cloneElement(children, {
    control,
    name,
    ...(hideLabel ? {} : { label }),
    errors,
    value,
    defaultValue: value,
    setValue,
    size: 'small',
    sx: fullHeight ? { ...inlineSx, height: '100%' } : inlineSx,
    boxSx: fullHeight
      ? { ...EditInlineBoxSx, height: '100%' }
      : EditInlineBoxSx,
    buttonGroupSx: fullHeight
      ? { ...EditInlineButtonGroupSx, height: '100%' }
      : EditInlineButtonGroupSx,
    autoFocus: true,
  });

  return (
    <>
      <form
        ref={formRef}
        onSubmit={handleSubmit(onSubmit)}
        style={{ width: '100%' }}
      >
        <GridLegacy
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          sx={{ marginTop: '10px' }}
          container
        >
          <GridLegacy item xs>
            {clonedChild}
          </GridLegacy>
          {!tempClickOffInlineEdit && (
            <GridLegacy item xs="auto">
              <SegmentInlineFieldButton
                loading={isLoading}
                type="submit"
                color="primary"
                permissionScope={permissionScope}
                trackingName={trackingNames?.save}
              />
            </GridLegacy>
          )}
          {!!tempClickOffInlineEdit && (
            <GridLegacy item xs="auto">
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SegmentIconButton
                  color="primary"
                  onClick={handleSubmit(onSubmit)}
                  size="small"
                  permissionScope={permissionScope}
                  trackingName={trackingNames?.save}
                  tooltip="Save value"
                >
                  <IconDeviceFloppy stroke="1.5px" width={20} height={20} />
                </SegmentIconButton>
              )}
            </GridLegacy>
          )}
          {closeEditing && (
            <GridLegacy item xs="auto">
              <SegmentIconButton
                color="error"
                onClick={closeEditing}
                size="small"
                permissionScope={permissionScope}
                trackingName={trackingNames?.cancel}
                tooltip={
                  tempClickOffInlineEdit
                    ? 'Exit editing without saving changes'
                    : 'Exit Edit'
                }
              >
                {tempClickOffInlineEdit ? (
                  <IconArrowBackUp stroke="1.5px" width={20} height={20} />
                ) : (
                  <IconX stroke="1.5px" width={20} height={20} />
                )}
              </SegmentIconButton>
            </GridLegacy>
          )}
        </GridLegacy>
      </form>
    </>
  );
};

export default EditInline;
